<template>
  <div>
    <!-- ── Desktop navbar (lg and up) ── -->
    <nav class="main-navbar" :class="{ 'navbar-scrolled': scrolled }">
      <div class="container navbar-inner">

        <!-- Brand -->
        <router-link class="nav-brand" to="/">
          <img src="/logo_navbar.png" alt="Beastmode Cleanup logo" class="nav-logo" />
          <span class="nav-brand-text">Beastmode Cleanup</span>
        </router-link>

        <!-- Desktop links -->
        <ul class="desktop-links">
          <li><router-link class="desktop-link" to="/" exact>Home</router-link></li>
          <li><router-link class="desktop-link" to="/gallery">Gallery</router-link></li>
          <li><router-link class="desktop-link" to="/blog">Blog</router-link></li>
          <li><router-link class="desktop-link" to="/aboutus">About Us</router-link></li>
          <li><router-link class="desktop-link" to="/contactus">Contact Us</router-link></li>
        </ul>

        <!-- Desktop social icons -->
        <div class="desktop-social">
          <a href="https://www.facebook.com/share/15DqxV8Apd/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" class="social-icon">
            <i class="fab fa-facebook-square" aria-hidden="true"></i>
          </a>
          <a href="https://www.linkedin.com/in/kimberly-lay-1899a6283" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" class="social-icon">
            <i class="fab fa-linkedin" aria-hidden="true"></i>
          </a>
        </div>

        <!-- Hamburger (mobile only) -->
        <button
          class="hamburger"
          :class="{ open: mobileOpen }"
          @click="toggleMobile"
          aria-label="Toggle navigation"
          :aria-expanded="mobileOpen.toString()"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

      </div>
    </nav>

    <!-- ── Mobile overlay menu ── -->
    <transition name="mobile-menu">
      <div v-if="mobileOpen" class="mobile-overlay" @click.self="closeMobile">
        <nav class="mobile-drawer">

          <!-- Drawer header -->
          <div class="mobile-drawer-header">
            <div class="mobile-brand-wrap">
              <img src="/logo_navbar.png" alt="Beastmode Cleanup logo" class="mobile-drawer-logo" />
              <span class="mobile-brand">Beastmode Cleanup</span>
            </div>
            <button class="mobile-close" @click="closeMobile" aria-label="Close navigation">
              <span>&#10005;</span>
            </button>
          </div>

          <!-- Nav links -->
          <ul class="mobile-links">
            <li>
              <router-link to="/" exact @click.native="closeMobile" class="mobile-link">
                <i class="now-ui-icons business_bank" aria-hidden="true"></i>
                Home
              </router-link>
            </li>
            <li>
              <router-link to="/gallery" @click.native="closeMobile" class="mobile-link">
                <i class="now-ui-icons media-1_album" aria-hidden="true"></i>
                Gallery
              </router-link>
            </li>
            <li>
              <router-link to="/blog" @click.native="closeMobile" class="mobile-link">
                <i class="now-ui-icons design_bullet-list-67" aria-hidden="true"></i>
                Blog
              </router-link>
            </li>
            <li>
              <router-link to="/aboutus" @click.native="closeMobile" class="mobile-link">
                <i class="now-ui-icons files_single-copy-04" aria-hidden="true"></i>
                About Us
              </router-link>
            </li>
            <li>
              <router-link to="/contactus" @click.native="closeMobile" class="mobile-link">
                <i class="now-ui-icons ui-1_email-85" aria-hidden="true"></i>
                Contact Us
              </router-link>
            </li>
          </ul>

          <!-- CTA -->
          <div class="mobile-cta">
            <router-link to="/contactus" class="mobile-cta-btn" @click.native="closeMobile">
              Get a Free Quote
            </router-link>
          </div>

          <!-- Social -->
          <div class="mobile-social">
            <a href="https://www.facebook.com/share/15DqxV8Apd/" target="_blank" rel="noopener noreferrer" aria-label="Facebook" class="mobile-social-icon">
              <i class="fab fa-facebook-square" aria-hidden="true"></i>
            </a>
            <a href="https://www.linkedin.com/in/kimberly-lay-1899a6283" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" class="mobile-social-icon">
              <i class="fab fa-linkedin" aria-hidden="true"></i>
            </a>
          </div>

        </nav>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'main-navbar',
  props: {
    transparent: Boolean,
    colorOnScroll: Number
  },
  data() {
    return {
      mobileOpen: false,
      scrolled: false,
    };
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = '';
  },
  watch: {
    $route() {
      this.closeMobile();
    }
  },
  methods: {
    handleScroll() {
      this.scrolled = window.scrollY > (this.colorOnScroll || 50);
    },
    toggleMobile() {
      this.mobileOpen ? this.closeMobile() : this.openMobile();
    },
    openMobile() {
      this.mobileOpen = true;
      document.body.style.overflow = 'hidden';
    },
    closeMobile() {
      this.mobileOpen = false;
      document.body.style.overflow = '';
    },
    handleKeydown(e) {
      if (e.key === 'Escape' && this.mobileOpen) this.closeMobile();
    }
  }
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Desktop Navbar
══════════════════════════════════════ */
.main-navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: transparent;
  transition: background 0.3s ease, box-shadow 0.3s ease;
}

.main-navbar.navbar-scrolled {
  background: #c0392b;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.18);
}

/* Always show solid bg on inner pages (no colorOnScroll hero) */
.main-navbar:not(.navbar-scrolled) {
  background: #c0392b;
}

.navbar-inner {
  display: flex;
  align-items: center;
  height: 70px;
  gap: 1.5rem;
}

/* Brand */
.nav-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.15rem;
  font-weight: 800;
  color: #ffffff !important;
  text-decoration: none;
  letter-spacing: 0.01em;
  white-space: nowrap;
  flex-shrink: 0;
}

.nav-brand:hover {
  color: rgba(255,255,255,0.85) !important;
}

.nav-logo {
  width: 42px;
  height: 42px;
  object-fit: contain;
  flex-shrink: 0;
}

.nav-brand-text {
  color: #ffffff;
}

/* Desktop links */
.desktop-links {
  display: flex;
  list-style: none;
  margin: 0 0 0 auto;
  padding: 0;
  gap: 0.25rem;
  justify-content: flex-end;
}

.desktop-link {
  display: block;
  padding: 6px 14px;
  font-size: 0.95rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.88) !important;
  text-decoration: none;
  border-radius: 4px;
  transition: color 0.15s, background 0.15s;
  white-space: nowrap;
}

.desktop-link:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.12);
}

/* ::v-deep needed because Vue Router adds the active class to the
   rendered <a> element, outside the scoped style boundary */
::v-deep .desktop-link.router-link-exact-active {
  color: #ffffff !important;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.18);
  border-bottom: 2px solid rgba(255, 255, 255, 0.7);
}

/* Desktop social */
.desktop-social {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.social-icon {
  color: rgba(255, 255, 255, 0.8);
  font-size: 1.25rem;
  text-decoration: none;
  transition: color 0.15s;
}

.social-icon:hover {
  color: #ffffff;
}

/* Hamburger — hidden on desktop */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  flex-shrink: 0;
}

.hamburger span {
  display: block;
  height: 2px;
  width: 100%;
  background: #ffffff;
  border-radius: 2px;
  transition: transform 0.25s ease, opacity 0.25s ease;
  transform-origin: center;
}

/* Animate to X when open */
.hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
.hamburger.open span:nth-child(2) { opacity: 0; transform: scaleX(0); }
.hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

/* Hide desktop elements on mobile */
@media (max-width: 991px) {
  .desktop-links,
  .desktop-social {
    display: none;
  }
  .hamburger {
    display: flex;
    margin-left: auto;
  }
}

/* ══════════════════════════════════════
   Mobile Overlay
══════════════════════════════════════ */
.mobile-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(10, 20, 50, 0.55);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
}

.mobile-drawer {
  position: absolute;
  top: 0;
  right: 0;
  width: min(320px, 85vw);
  height: 100%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  box-shadow: -4px 0 32px rgba(0, 0, 0, 0.18);
}

/* Drawer header */
.mobile-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  height: 70px;
  background: #c0392b;
  flex-shrink: 0;
}

.mobile-brand-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.mobile-drawer-logo {
  width: 38px;
  height: 38px;
  object-fit: contain;
  flex-shrink: 0;
}

.mobile-brand {
  font-size: 1rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: 0.01em;
}

.mobile-close {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.85);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  transition: color 0.15s;
}

.mobile-close:hover {
  color: #ffffff;
}

/* Nav links */
.mobile-links {
  list-style: none;
  margin: 0;
  padding: 12px 0;
  flex: 1;
}

.mobile-links li {
  border-bottom: 1px solid #f0f0f0;
}

.mobile-links li:first-child {
  border-top: 1px solid #f0f0f0;
}

.mobile-link {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 24px;
  font-size: 1.05rem;
  font-weight: 600;
  color: #1a1a2e !important;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}

.mobile-link i {
  font-size: 1.1rem;
  color: #c0392b;
  width: 20px;
  text-align: center;
  flex-shrink: 0;
}

.mobile-link:hover,
.mobile-link.router-link-exact-active {
  background: #fdf0ef;
  color: #c0392b !important;
}

.mobile-link.router-link-exact-active {
  font-weight: 700;
}

/* CTA button */
.mobile-cta {
  padding: 20px 24px 8px;
}

.mobile-cta-btn {
  display: block;
  width: 100%;
  text-align: center;
  background: #c0392b;
  color: #ffffff !important;
  font-size: 1rem;
  font-weight: 700;
  padding: 14px;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s;
}

.mobile-cta-btn:hover {
  background: #96231a;
}

/* Social row */
.mobile-social {
  display: flex;
  justify-content: center;
  gap: 1.25rem;
  padding: 20px 24px 32px;
}

.mobile-social-icon {
  color: #c0392b;
  font-size: 1.6rem;
  text-decoration: none;
  transition: color 0.15s;
}

.mobile-social-icon:hover {
  color: #96231a;
}

/* ── Slide transition ── */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.25s ease;
}

.mobile-menu-enter-active .mobile-drawer,
.mobile-menu-leave-active .mobile-drawer {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.mobile-menu-enter .mobile-drawer,
.mobile-menu-leave-to .mobile-drawer {
  transform: translateX(100%);
}

.mobile-menu-enter,
.mobile-menu-leave-to {
  opacity: 0;
}
</style>
